import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// @mui
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  FormControlLabel,
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableHead,
  TableRow,
  TableCell,
  Button,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';

// hooks
import useSettings from '../../../hooks/useSettings';
import useAuth from '../../../hooks/useAuth';

// redux-slice
import { getProfitSummary } from '../../../redux/slices/operator';
import { getUsersByRole } from '../../../redux/slices/suspiciousActivity';

// components
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';

// ----------------------------------------------------------------------
// Indian currency formatter
const formatIndianCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0';

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

// ----------------------------------------------------------------------
// Styled components
const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  alignItems: 'center',
  flexWrap: 'wrap',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 0, // Sharp corners
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    '& > *': {
      width: '100%',
    },
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 800,
    '& .MuiTableCell-root': {
      border: `1px solid ${theme.palette.divider}`,
      borderTop: 'none',
      borderLeft: 'none',
      '&:first-of-type': {
        borderLeft: `1px solid ${theme.palette.divider}`,
      },
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
    },
  },
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  borderRadius: 0, // Sharp corners
  '& .MuiTypography-root': {
    color: 'inherit',
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiBox-root': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
  },
}));

const ResponsiveCard = styled(Card)(({ theme }) => ({
  borderRadius: 0, // Sharp corners
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0, -2),
    borderRadius: 0,
  },
}));

// Role-based dropdown configuration
const getUserDropdowns = (userRole, users) => {
  switch (userRole) {
    case 'superAdmin':
      return [
        { key: 'admin', label: 'Select Admin', users: users.admin || [] },
        { key: 'broker', label: 'Select Broker', users: users.broker || [] },
        { key: 'client', label: 'Select Client', users: users.client || [] },
      ];
    case 'admin':
      return [
        { key: 'broker', label: 'Select Broker', users: users.broker || [] },
        { key: 'client', label: 'Select Client', users: users.client || [] },
      ];
    case 'broker':
      return [{ key: 'client', label: 'Select Client', users: users.client || [] }];
    default:
      return [];
  }
};

// Utility functions for week calculations
const getAvailableRoles = (summaryData) => {
  if (!summaryData) return [];

  const availableRoles = [];
  if (summaryData.totalGrossProfitLoss?.client !== undefined) availableRoles.push('client');
  if (summaryData.totalGrossProfitLoss?.broker !== undefined) availableRoles.push('broker');
  if (summaryData.totalGrossProfitLoss?.admin !== undefined) availableRoles.push('admin');
  if (summaryData.totalGrossProfitLoss?.superAdmin !== undefined) availableRoles.push('superAdmin');

  return availableRoles;
};

const getWeekRange = (week, year) => {
  // ISO week calculation - weeks start on Monday
  // ISO week 1 is the first week that contains at least 4 days of the new year
  // This means the first Thursday of the year is always in week 1

  // Find January 4th (always in week 1)
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Calculate days to get to Monday (start of ISO week)
  // If Jan 4 is Sunday (0), we need to go back 6 days to Monday
  // If Jan 4 is Monday (1), we need to go back 0 days
  // If Jan 4 is Tuesday (2), we need to go back 1 day, etc.
  const daysToMonday = jan4DayOfWeek === 0 ? 6 : jan4DayOfWeek - 1;

  // Calculate the start of ISO week 1 (Monday)
  const week1Start = new Date(jan4);
  week1Start.setDate(jan4.getDate() - daysToMonday);

  // Calculate the start of the requested week
  const weekStart = new Date(week1Start);
  weekStart.setDate(week1Start.getDate() + (week - 1) * 7);

  // Calculate the end of the requested week (Sunday)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  return {
    start: weekStart,
    end: weekEnd,
  };
};

const getBillingDate = (week, year) => new Date(getWeekRange(week, year).end);

const formatDate = (date) =>
  date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });

const formatWeekRange = (week, year) => {
  const range = getWeekRange(week, year);
  return `${formatDate(range.start)} to ${formatDate(range.end)}`;
};

// ----------------------------------------------------------------------

export default function ClientLedgersReport() {
  const { themeStretch } = useSettings();
  const dispatch = useDispatch();
  const { user, userType: authUserType } = useAuth();

  // Extract the actual role string from user object
  const userRole =
    user?.role?.rolename ||
    (typeof authUserType === 'object'
      ? authUserType?.role || authUserType?.userType || authUserType?.type || authUserType
      : authUserType);

  // Redux selectors
  const suspiciousActivityState = useSelector((state) => state.suspiciousActivity || {});
  const { users = {}, usersLoaded = {} } = suspiciousActivityState;
  const operatorState = useSelector((state) => state.operator || {});
  const { profitSummary } = operatorState;

  // Local state for filters (single value selection)
  const [userFilters, setUserFilters] = useState({
    admin: null,
    broker: null,
    client: null,
  });

  // Additional filter states
  const [dateRange, setDateRange] = useState('Last 3 Months');
  const [showSharingFigures, setShowSharingFigures] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Date range options
  const dateRangeOptions = [
    'Last 3 Weeks',
    'Last 6 Weeks',
    'Last 3 Months',
    'Last 6 Months',
    'Last Year',
    'Custom Date',
  ];

  // Generate API payload for profit summary
  const generateApiPayload = () => {
    const getDuration = () => {
      switch (dateRange) {
        case 'Last 3 Weeks':
          return '3WEEK';
        case 'Last 6 Weeks':
          return '6WEEK';
        case 'Last 3 Months':
          return '3MONTH';
        case 'Last 6 Months':
          return '6MONTH';
        case 'Last Year':
          return '1YEAR';
        case 'Custom Date':
          return 'CUSTOM';
        default:
          return '3WEEK';
      }
    };

    const payload = {
      showSharing: showSharingFigures,
      duration: getDuration(),
      client: userFilters.client ? userFilters.client.id || userFilters.client._id || userFilters.client : null,
      broker: userFilters.broker ? userFilters.broker.id || userFilters.broker._id || userFilters.broker : null,
      admin: userFilters.admin ? userFilters.admin.id || userFilters.admin._id || userFilters.admin : null,
    };

    // Add startDate and endDate for custom duration
    if (dateRange === 'Custom Date' && startDate && endDate) {
      payload.startDate = startDate.toISOString().split('T')[0];
      payload.endDate = endDate.toISOString().split('T')[0];
    }

    return payload;
  };

  // Handle Show Result button click
  const handleShowResults = async () => {
    const apiPayload = generateApiPayload();

    try {
      // Call the profit summary API
      const result = await dispatch(getProfitSummary(apiPayload));

      if (result.success) {
        setShowResults(true);
        console.log('Profit Summary Data:', result.data);
      } else {
        console.error('Error fetching profit summary:', result.error);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  // Calculate totals based on profit summary data
  const calculatedGrossPL =
    showResults && profitSummary.data
      ? profitSummary.data.summary.totalGrossProfitLoss[profitSummary.data.targetUser.role] || 0
      : 0;

  // Get data to display from profit summary
  const displayData = showResults && profitSummary.data ? profitSummary.data.weeklyData : [];

  // Handle date range change
  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'Custom Date') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  // Fetch users based on user role
  useEffect(() => {
    const rolesToFetch = [];

    switch (userRole) {
      case 'superAdmin':
        rolesToFetch.push('admin', 'broker', 'client');
        break;
      case 'admin':
        rolesToFetch.push('broker', 'client');
        break;
      case 'broker':
        rolesToFetch.push('client');
        break;
      default:
        break;
    }

    // Fetch each role's users
    rolesToFetch.forEach((role) => {
      if (!usersLoaded[role]) {
        dispatch(getUsersByRole(role, {}, true));
      }
    });
  }, [dispatch, userRole, usersLoaded]);

  // Hierarchical refetching: admin selection changes (superAdmin only)
  useEffect(() => {
    if (userRole === 'superAdmin' && userFilters.admin) {
      // Extract admin ID from the single selected admin
      const adminId = userFilters.admin.id || userFilters.admin._id || userFilters.admin;

      if (adminId) {
        // Fetch brokers filtered by selected admin
        dispatch(getUsersByRole('broker', { admin: [adminId] }, true));

        // Fetch clients filtered by selected admin only
        dispatch(getUsersByRole('client', { admin: [adminId] }, true));
      } else {
        // If no admin selected, fetch all brokers and clients
        dispatch(getUsersByRole('broker', {}, true));
        dispatch(getUsersByRole('client', {}, true));
      }
    }
  }, [dispatch, userRole, userFilters.admin]);

  // Hierarchical refetching: broker selection changes
  useEffect(() => {
    if ((userRole === 'superAdmin' || userRole === 'admin') && userFilters.broker) {
      // Build query params for client fetch
      const clientParams = {};

      // Add admin filter if superAdmin role and admin is selected
      if (userRole === 'superAdmin' && userFilters.admin) {
        const adminId = userFilters.admin.id || userFilters.admin._id || userFilters.admin;
        clientParams.admin = [adminId];
      }

      // Add broker filter
      const brokerId = userFilters.broker.id || userFilters.broker._id || userFilters.broker;
      clientParams.broker = [brokerId];

      // Fetch clients filtered by selected broker (and optionally admin)
      dispatch(getUsersByRole('client', clientParams, true));
    } else if (userRole === 'admin' && !userFilters.broker) {
      // If admin role and no broker selected, fetch all clients under this admin
      dispatch(getUsersByRole('client', {}, true));
    }
  }, [dispatch, userRole, userFilters.broker, userFilters.admin]);

  // Handle user filter changes with hierarchical reset logic (single value)
  const handleUserFilterChange = (filterType, newValue) => {
    console.log('Filter change:', filterType, newValue);

    // Hierarchical reset logic for single values
    if (filterType === 'admin') {
      // Reset broker and client when admin changes
      setUserFilters((prev) => ({ ...prev, admin: newValue, broker: null, client: null }));
    } else if (filterType === 'broker') {
      // Reset client when broker changes
      setUserFilters((prev) => ({ ...prev, broker: newValue, client: null }));
    } else {
      // Direct update for client
      setUserFilters((prev) => ({ ...prev, [filterType]: newValue }));
    }
  };

  // Get dropdowns based on user role
  const dropdowns = getUserDropdowns(userRole, users);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page title="Client Ledgers Report">
        <Container maxWidth={themeStretch ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
          <HeaderBreadcrumbs
            heading="Client Ledgers Report"
            links={[{ name: 'Dashboard', href: '/dashboard/operator' }, { name: 'Client Ledgers Report' }]}
          />

          {/* All Filters - Responsive Layout */}
          <FilterContainer>
            <Grid container spacing={2} alignItems="center">
              {/* User Role Filters */}
              {dropdowns.map((dropdown) => (
                <Grid item xs={12} sm={6} md={3} key={dropdown.key}>
                  <Autocomplete
                    size="small"
                    options={dropdown.users}
                    getOptionLabel={(option) => option.username || option.name || option}
                    value={userFilters[dropdown.key]}
                    onChange={(event, newValue) => handleUserFilterChange(dropdown.key, newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label={dropdown.label} placeholder={dropdown.label} />
                    )}
                    fullWidth
                    clearOnEscape
                    clearOnBlur
                  />
                </Grid>
              ))}

              {/* Date Range Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select value={dateRange} label="Date Range" onChange={(e) => handleDateRangeChange(e.target.value)}>
                    {dateRangeOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Custom Date Pickers */}
              {dateRange === 'Custom Date' && (
                <>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                    />
                  </Grid>
                </>
              )}

              {/* Show Sharing Figures Checkbox */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showSharingFigures}
                      onChange={(e) => setShowSharingFigures(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show Sharing Figures"
                />
              </Grid>

              {/* Show Result Button */}
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleShowResults}
                  disabled={
                    profitSummary.isLoading ||
                    (dateRange === 'Custom Date' && (!startDate || !endDate)) ||
                    (!userFilters.admin && !userFilters.broker && !userFilters.client)
                  }
                  startIcon={profitSummary.isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                  fullWidth
                  sx={{ borderRadius: 0 }}
                >
                  {profitSummary.isLoading ? 'Loading...' : 'Show Result'}
                </Button>
              </Grid>

              {/* User Type Display */}
              {/* {userRole && !isMobile && (
                                <Grid item xs={12} md={1}>
                                    <Typography variant="body2" color="text.secondary" align="right">
                                        User Type: <strong>{userRole}</strong>
                                    </Typography>
                                </Grid>
                            )} */}
            </Grid>
          </FilterContainer>

          {/* Header Information */}
          {showResults && profitSummary.data && (
            <HeaderCard>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="h6">
                    <strong>{profitSummary.data.targetUser.username}</strong> ({profitSummary.data.targetUser.firstname}{' '}
                    {profitSummary.data.targetUser.lastname})
                  </Typography>
                  <Typography variant="h6">- {profitSummary.data.duration.type}</Typography>
                </Box>
                <Typography variant="h6">
                  Gross PL:{' '}
                  <strong
                    style={{
                      color: calculatedGrossPL >= 0 ? theme.palette.success.light : theme.palette.error.main,
                    }}
                  >
                    {calculatedGrossPL >= 0 ? '+' : ''}
                    {formatIndianCurrency(calculatedGrossPL)}
                  </strong>
                </Typography>
              </Box>
            </HeaderCard>
          )}

          {/* Results Table */}
          <ResponsiveCard>
            <StyledTableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Trading Week</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Billing Date</TableCell>
                    {showResults && profitSummary.data && (
                      <>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Gross PL
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Brokerage
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          Booked PL
                        </TableCell>
                        {showSharingFigures && profitSummary.data.summary && (
                          <>
                            {getAvailableRoles(profitSummary.data.summary)
                              .filter((role) => role !== profitSummary.data.targetUser.role)
                              .map((role) => (
                                <TableCell key={role} align="right" sx={{ textTransform: 'capitalize', minWidth: 120 }}>
                                  {role} Gross PL
                                </TableCell>
                              ))}
                          </>
                        )}
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!showResults && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          Please click "Show Result" to view the data
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {showResults && (!profitSummary.data || displayData.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No data found for the selected filters
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {showResults &&
                    profitSummary.data &&
                    displayData.length > 0 &&
                    displayData.map((row, index) => {
                      const targetRole = profitSummary.data.targetUser.role;
                      const grossPL = row.totalGrossProfitLoss[targetRole] || 0;
                      const brokerage = row.totalBrokerage[targetRole] || 0;
                      const bookedPL = row.totalBookedProfitLoss[targetRole] || 0;

                      return (
                        <TableRow key={index} hover>
                          <TableCell sx={{ minWidth: 120 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {formatWeekRange(row.week, row.year)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 100 }}>
                            <Typography variant="body2">{formatDate(getBillingDate(row.week, row.year))}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={grossPL >= 0 ? 'success.main' : 'error.main'}
                            >
                              {grossPL >= 0 ? '+' : ''}
                              {formatIndianCurrency(grossPL)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            <Typography variant="body2">{formatIndianCurrency(brokerage)}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={bookedPL >= 0 ? 'success.main' : 'error.main'}
                            >
                              {bookedPL >= 0 ? '+' : ''}
                              {formatIndianCurrency(bookedPL)}
                            </Typography>
                          </TableCell>
                          {showSharingFigures && profitSummary.data.summary && (
                            <>
                              {getAvailableRoles(profitSummary.data.summary)
                                .filter((role) => role !== targetRole)
                                .map((role) => (
                                  <TableCell key={role} align="right" sx={{ minWidth: 120 }}>
                                    <Typography
                                      variant="body2"
                                      color={(row.totalGrossProfitLoss[role] || 0) >= 0 ? 'success.main' : 'error.main'}
                                    >
                                      {(row.totalGrossProfitLoss[role] || 0) >= 0 ? '+' : ''}
                                      {formatIndianCurrency(row.totalGrossProfitLoss[role] || 0)}
                                    </Typography>
                                  </TableCell>
                                ))}
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </ResponsiveCard>
        </Container>
      </Page>
    </LocalizationProvider>
  );
}
